import { useAuthStore } from '@/store/auth';
import { Permission, UserRole } from '@/types';
import {
  canCompleteProfile,
  getGuideLifecycle,
  getGuideStatus,
  hasActiveGuideApplication,
  hasGuideProfile,
  isApprovedGuide,
  isAwaitingVerification,
  isGuidePendingReview,
  isSuspendedGuide,
  isVerificationFailed,
  needsOnboarding,
  resolveGuideLifecycleRoute,
} from '@/features/auth/utils/resolvePostAuthRoute';

export function useAuth() {
  const store = useAuthStore();

  const approvedGuide = isApprovedGuide(store.user, store.permissions);
  const pendingReview = isGuidePendingReview(store.user);
  const awaitingVerification = isAwaitingVerification(store.user);

  return {
    ...store,
    // ─── Guide lifecycle ───
    guideLifecycle: getGuideLifecycle(store.user),
    /** Where this user belongs, or null if they have no guide application. */
    guideLifecycleRoute: resolveGuideLifecycleRoute(store.user),
    hasGuideProfile: hasGuideProfile(store.user),
    hasActiveGuideApplication: hasActiveGuideApplication(store.user),
    isApprovedGuide: approvedGuide,
    isGuidePendingReview: pendingReview,
    isAwaitingVerification: awaitingVerification,
    isVerificationFailed: isVerificationFailed(store.user),
    isSuspendedGuide: isSuspendedGuide(store.user),
    canCompleteProfile: canCompleteProfile(store.user),
    needsOnboarding: needsOnboarding(store.user),
    isGuideApplicant: hasActiveGuideApplication(store.user) && !approvedGuide,

    // ─── Roles ───
    isGuide: approvedGuide,
    isAdmin: store.user?.role === UserRole.SUPER_ADMIN,
    isTraveler: store.user?.role === UserRole.TRAVELER,
    canManageGuideProfile: store.permissions.includes(Permission.MANAGE_GUIDE_PROFILE),

    accountLabel: store.user?.role === UserRole.SUPER_ADMIN
      ? 'Admin'
      : approvedGuide
        ? 'Guide'
        : pendingReview
          ? 'Under Review'
          : awaitingVerification
            ? 'Verification Pending'
            : hasActiveGuideApplication(store.user)
              ? 'Guide Applicant'
              : 'Traveler',

    /** @deprecated Legacy status accessor. */
    getGuideStatus: () => getGuideStatus(store.user),
  };
}
